/**
 * Store pour la liste des audiences IA de l'utilisateur que l'on gère
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
/***/
import { useUserStore } from '@stores/user';
export const useAuthAIAudienceStore = defineStore('authAIAudience', (() => {

    const userStore = useUserStore();

    // Liste des audiences
    const audiencesByUserId = ref({});

    // Retourne l'utilisateur que l'on gère
    const getManagingUser = computed(() => userStore.getCurrentManagingUser);

    // Retourne les audiences de l'utilisateur que l'on gère
    const getAudiencesOfManagingUser = computed(() => {
        const user = getManagingUser.value;
        const userAudiences = (audiencesByUserId.value[user.id] ?? null);
        return userAudiences;
    })


    // Retourne si la liste des audiences a été chargée
    const loaded = computed(() => (getAudiencesOfManagingUser.value !== null));

    // Retourne la liste des audiences
    const getList = computed(() => (getAudiencesOfManagingUser.value ?? []));

    // Retourne la liste des audiences du réseau en paramètre
    const getListOfNetwork = computed(() => {
        return ((network) => getList.value.filter((audience) => audience.network === network));
    });

    // Retourne l'audience dont l'identifiant est en paramètre
    const getAudienceById = computed(() => {
        return ((id) => {
            const item = getList.value.filter((audience) => audience.id === id).at(0);
            return (item ?? null);
        });
    });

    /**
     * Modifie les audiences par celles en paramètre
     * @param Array newAudiences
     * @return void
     */
    const setAudiences = ((newAudiences) => {
        const userId = getManagingUser.value.id;
        audiencesByUserId.value[userId] = newAudiences;
    });

    /**
     * Chargement de la liste des audiences
     * @param boolean force Vrai si on force le chargement
     * @return Promise
     */
    const loadAudiences = ((force = false) => {

        if(! force && getAudiencesOfManagingUser.value !== null) {
            return Promise.resolve();
        }

        const userId = getManagingUser.value.id;

        const path = `/api/users/${ userId }/ai-audiences`;

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.aiAudiences) {
                audiencesByUserId.value[userId] = response.data.aiAudiences;
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Ajout d'une audience
     * @param Object postParams
     * @return Promise
     */
    const addAudience = ((postParams) => {
        const userId = getManagingUser.value.id;
        const path = `/api/users/${ userId }/ai-audiences`;

        return axios.post(path, postParams).then((response) => {
            if(response && response.data && response.data.success && response.data.aiAudience) {
                let newAudience = null;
                if(getAudiencesOfManagingUser.value !== null) {
                    audiencesByUserId.value[userId].push(response.data.aiAudience);
                    newAudience = getAudienceById.value(response.data.aiAudience.id);
                }
                return Promise.resolve(newAudience);
            }
            return Promise.reject();
        }).catch((response) => {
            return Promise.reject(response);
        });
    });

    /**
     * Mise à jour de l'audience en paramètre
     * @param int audienceId
     * @param Object postParams
     * @return Promise
     */
    const updateAudience = ((audienceId, postParams) => {

        const userId = getManagingUser.value.id;

        const path = `/api/users/${ userId }/ai-audiences/${ audienceId }`;

        return axios.post(path, postParams).then((response) => {
            if(response && response.data && response.data.success && response.data.aiAudience) {
                if(getAudiencesOfManagingUser.value !== null) {
                    const indexToUpdate = audiencesByUserId.value[userId].findIndex((currentAudience) => currentAudience.id === audienceId);
                    if(indexToUpdate !== -1) {
                        audiencesByUserId.value[userId][indexToUpdate] = response.data.aiAudience;
                    }
                }
                return Promise.resolve();
            }
            return Promise.reject();
        }).catch((response) => {
            return Promise.reject(response);
        });

    });

    /**
     * Suppression de l'audience en paramètre
     * @param Object audience
     * @return Promise
     */
    const remove = ((audience) => {

        const userId = getManagingUser.value.id;

        const path = `/api/users/${ userId }/ai-audiences/${ audience.id }`;

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                if(getAudiencesOfManagingUser.value !== null) {
                    const indexToDelete = audiencesByUserId.value[userId].findIndex((currentAudience) => currentAudience.id === audience.id);
                    if(indexToDelete !== -1) {
                        // Utilisation d'un timer le temps d'appeler les événements dans les composants
                        setTimeout(() => {
                            audiencesByUserId.value[userId].splice(indexToDelete, 1);
                        }, 200);

                    }
                }
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Réinitialisation de la liste des audiences
     * @return void
     */
    const reset = (() => {
        audiencesByUserId.value = {};
    });

    return {
        loaded,
        getList,
        getListOfNetwork,
        getAudienceById,
        setAudiences,
        loadAudiences,
        addAudience,
        updateAudience,
        remove
    };

}));


